// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { buildSchemas } from '../validation/schemas';
import notify from '../utils/toast';

export default function Login() {
    const { login } = useAuth();
    const { mode, toggleMode } = useTheme();
    const { t } = useI18n();
    const navigate = useNavigate();
    const [showPass, setShowPass] = useState(false);
    const schemas = buildSchemas(t);

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(schemas.login),
        defaultValues: { email: '', password: '' },
    });

    const onSubmit = async (data) => {
        try {
            const user = await login(data.email, data.password);
            notify.success(t('welcome'));
            navigate(user.role === 'customer' ? '/portal' : '/dashboard');
        } catch (err) {
            notify.apiError(err, t('failed'));
        }
    };

    const fillDemo = (role) => {
        const map = {
            super: ['superadmin@ssrem.com', 'Super@123'],
            admin: ['admin@ssrem.com', 'Admin@123'],
            sales: ['sales@ssrem.com', 'Sales@123'],
            finance: ['finance@ssrem.com', 'Finance@123'],
            customer: ['customer@ssrem.com', 'Customer@123'],
        };
        setValue('email', map[role][0], { shouldValidate: true });
        setValue('password', map[role][1], { shouldValidate: true });
    };

    return (
        <div className="login-page">
            <button type="button" className="btn btn-sm login-theme-btn" onClick={toggleMode} title={t('theme')}>
                <i className={`bi ${mode === 'light' ? 'bi-moon-stars' : 'bi-sun'}`} />
            </button>

            <div className="login-shell">
                <div className="login-hero d-none d-lg-flex">
                    <div className="login-hero-inner">
                        <div className="login-hero-badge">
                            <i className="bi bi-house-heart-fill" />
                        </div>
                        <h1 className="login-hero-title">{t('appName')}</h1>
                        <p className="login-hero-sub">{t('overview')}</p>
                        <ul className="login-hero-list">
                            <li>
                                <i className="bi bi-check2-circle" /> {t('inventory')}
                            </li>
                            <li>
                                <i className="bi bi-check2-circle" /> {t('bookings')}
                            </li>
                            <li>
                                <i className="bi bi-check2-circle" /> {t('materials')}
                            </li>
                            <li>
                                <i className="bi bi-check2-circle" /> {t('accounts')}
                            </li>
                        </ul>
                        <div className="login-hero-foot">
                            {t('appNameShort')} · {t('version')} 1.5.7
                        </div>
                    </div>
                </div>

                <div className="login-form-pane">
                    <div className="login-card-box">
                        <div className="text-center mb-4 d-lg-none">
                            <i className="bi bi-house-heart-fill login-mobile-icon" />
                            <h4 className="mt-2 mb-0 fw-bold">{t('appNameShort')}</h4>
                            <p className="text-muted small mb-0">{t('appName')}</p>
                        </div>

                        <h2 className="login-welcome">{t('welcomeBack')}</h2>
                        <p className="text-muted small mb-4">{t('signInContinue')}</p>

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            <div className="mb-3">
                                <label className="form-label small fw-semibold">{t('email')}</label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-envelope" />
                                    </span>
                                    <input
                                        type="email"
                                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                        autoComplete="username"
                                        {...register('email')}
                                    />
                                </div>
                                {errors.email && <div className="invalid-feedback d-block">{errors.email.message}</div>}
                            </div>
                            <div className="mb-3">
                                <label className="form-label small fw-semibold">{t('password')}</label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-lock" />
                                    </span>
                                    <input
                                        type={showPass ? 'text' : 'password'}
                                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                        autoComplete="current-password"
                                        {...register('password')}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setShowPass((s) => !s)}
                                        title={showPass ? t('hidePassword') : t('showPassword')}
                                    >
                                        <i className={`bi ${showPass ? 'bi-eye-slash' : 'bi-eye'}`} />
                                    </button>
                                </div>
                                {errors.password && (
                                    <div className="invalid-feedback d-block">{errors.password.message}</div>
                                )}
                            </div>
                            <button type="submit" className="btn btn-primary w-100 py-2 fw-semibold" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <span className="spinner-border spinner-border-sm me-2" />
                                ) : (
                                    <i className="bi bi-box-arrow-in-right me-2" />
                                )}
                                {t('login')}
                            </button>
                        </form>

                        <hr className="my-4" />
                        <p className="text-muted small text-center mb-2">{t('demoLogin')}</p>
                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                            {['super', 'admin', 'sales', 'finance', 'customer'].map((r) => (
                                <button
                                    key={r}
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => fillDemo(r)}
                                >
                                    {t(r)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
